import React from 'react';
import styles from './TradeRecommendationForm.module.css';
import Input from '../../common/Input/Input';
import Select from '../../common/Select/Select';
import Textarea from '../../common/textArea/TextArea';
import { VALIDATION_MESSAGES } from '../../../utils/constants';

// Presentational field grid for the Trade Recommendation form.
// Holds no logic of its own — all values and handlers arrive via props.
const TradeFormFields = ({
  register,                     // react-hook-form: connects an input to the form
  errors,                       // current validation errors (for inline messages)
  setValue,                     // sets a field value from code (used by script search)
  tradeRecommendationIdOptions, // Buy / Sell options
  tradeTypeIdOptions,           // trade-type options (from server)
  targetStatusOptions,          // status options (Target Hit / Live / ...)
  scriptTypeOptions,            // script-type options (Equity / Futures / Options)
  exitTypeIdOptions,            // Manual / Auto exit options
  scriptSearchTerm,             // text currently shown in the script search box
  setScriptSearchTerm,          // updates the script search text
  internalTokens,               // list of tradeable stocks to search through
  isLiveStatus,                 // true when status is "Live" (locks exit fields)
  watchStatus,                  // live value of the status field
  formatStockLabel,             // formats a stock into a readable label
}) => {
  // Builds a numeric price input (Entry Price, Target 1, Stop Loss) so the
  // three near-identical fields share one definition instead of repeating markup.
  const renderPriceFieldWithLtp = ({
    fieldName,
    label,
    placeholder,
    validation,
    error,
    required = false,
  }) => (
    <div className={styles.priceFieldWrapper}>
      <Input
        label={label}
        type="number"
        step="0.01"
        placeholder={placeholder}
        error={error}
        {...register(fieldName, validation)}
        required={required}
      />
      {/* <button
        type="button"
        className={styles.ltpButton}
        onClick={() => setFieldFromLtp(fieldName)}
        disabled={currentLtp == null || Number.isNaN(Number(currentLtp))}
        title={
          currentLtp == null
            ? "Select script and wait for live price"
            : `Use LTP ${Number(currentLtp).toFixed(2)}`
        }
      >
        {currentLtp ? "🟢" : "🔴"}
      </button> */}
    </div>
  );

  return (
        // 6-column grid; empty <div /> blocks below are intentional layout spacers.
        <div
          className={`${styles.formGrid} ${styles.formGrid6Col}`}
        >
          {/* --- ROW 1 --- */}
          {/* Category: Buy or Sell (drives the profit-direction later) */}
          <div className={styles.formGroup}>
            <Select
              label="Category"
              required
              options={tradeRecommendationIdOptions}
              placeholder="Select recommendation"
              error={errors.tradeRecommendationId?.message}
              {...register("tradeRecommendationId", {
                required: VALIDATION_MESSAGES.REQUIRED,
              })}
            />
          </div>
          {/* Entry date of the recommendation */}
          <div className={styles.formGroup}>
            <Input
              label="Entry Date"
              type="date"
              placeholder="Select recommendation date"
              error={errors.recoDate?.message}
              {...register("recoDate", {
                required: VALIDATION_MESSAGES.REQUIRED,
              })}
              required
            />
          </div>
          {/* Entry time of the recommendation */}
          <div className={styles.formGroup}>
            <Input
              label="Time"
              type="time"
              placeholder="Select recommendation time"
              error={errors.recoTime?.message}
              {...register("recoTime", {
                required: VALIDATION_MESSAGES.REQUIRED,
              })}
              required
            />
          </div>
          {/* Trade type (loaded from server) */}
          <div className={styles.formGroup}>
            <Select
              label="Type"
              required
              options={tradeTypeIdOptions}
              placeholder="Select trade type"
              error={errors.tradeTypeId?.message}
              {...register("tradeTypeId", {
                required: VALIDATION_MESSAGES.REQUIRED,
              })}
            />
          </div>
          {/* Planned (recommended) exit date */}
          <div className={styles.formGroup}>
            <Input
              label="Reco Exit Date"
              type="date"
              placeholder="Select exit date"
              {...register("recoExitDate", {
                required: VALIDATION_MESSAGES.REQUIRED,
              })}
              required
            />
          </div>
          {/* Trade status; "Live" locks/clears the exit fields below */}
          <div className={styles.formGroup}>
            <Select
              label="Status"
              options={targetStatusOptions}
              placeholder="Select status"
              error={errors.status?.message}
              {...register("status", {
                required: VALIDATION_MESSAGES.REQUIRED,
              })}
              required
            />
          </div>
          {/* --- ROW 2 --- */}
          {/* Script search: a visible text box that fills two hidden fields
              (script symbol + token id). The typed text itself is not submitted. */}
          <div className={styles.formGroup}>
            <label
              className={`${styles.label} ${styles.scriptLabel}`}
            >
              Script <span className={styles.requiredStar}>*</span>
            </label>
            <input
              type="text"
              list="scriptOptions"
              placeholder="Search script..."
              className={`${styles.input} ${errors.script ? styles.scriptInputError : styles.scriptInputNormal}`}
              value={scriptSearchTerm}
              onChange={(e) => {
                const val = e.target.value;
                setScriptSearchTerm(val);
                // Try to match the typed text to a real stock in any label form.
                const tokenObj = internalTokens.find(
                  (t) =>
                    t.symbol === val ||
                    t.name === val ||
                    `${t.symbol} [${t.token}]` === val ||
                    `${t.symbol} (${t.name})` === val ||
                    formatStockLabel(t) === val,
                );
                if (tokenObj) {
                  // Match found: store the symbol + token behind the scenes.
                  setScriptSearchTerm(formatStockLabel(tokenObj));
                  setValue("script", tokenObj.symbol, { shouldValidate: true });
                  setValue("token", tokenObj.token, { shouldValidate: true });
                } else {
                  // No match: clear both so an invalid stock can't be submitted.
                  setValue("script", "", { shouldValidate: true });
                  setValue("token", "", { shouldValidate: true });
                }
              }}
            />
            {/* Browser autocomplete list built from the loaded stocks */}
            <datalist id="scriptOptions">
              {internalTokens.map((token) => (
                <option
                  key={token.token}
                  value={formatStockLabel(token)}
                />
              ))}
            </datalist>
            {errors.script && (
              <span
                className={styles.fieldError}
              >
                {errors.script.message}
              </span>
            )}
            {/* Hidden fields that actually hold the chosen stock's values */}
            <input
              type="hidden"
              {...register("script", {
                required: VALIDATION_MESSAGES.REQUIRED,
              })}
            />
            <input
              type="hidden"
              {...register("token")}
            />
          </div>
          {/* Market segment: Equity / Futures / Options (picks notification audience) */}
          <div className={styles.formGroup}>
            <Select
              label="Script Type"
              options={scriptTypeOptions}
              placeholder="Select script type"
              error={errors.scriptTypeId?.message}
              {...register("scriptTypeId", {
                required: VALIDATION_MESSAGES.REQUIRED,
              })}
              required
            />
          </div>
          {/* Entry price — base value for all profit calculations */}
          <div className={styles.formGroup}>
            {renderPriceFieldWithLtp({
              fieldName: "recoPriceLow",
              label: "Entry Price",
              placeholder: "Enter low price",
              validation: {
                required: VALIDATION_MESSAGES.REQUIRED,
                min: { value: 0, message: "Price must be >= 0" },
              },
              error: errors.recoPriceLow?.message,
              required: true,
            })}
          </div>
          {/* Primary profit target */}
          <div className={styles.formGroup}>
            {renderPriceFieldWithLtp({
              fieldName: "targetOne",
              label: "Target 1",
              placeholder: "Enter target 1",
              validation: {
                required: VALIDATION_MESSAGES.REQUIRED,
                min: { value: 0.01, message: "Target must be > 0" },
              },
              error: errors.targetOne?.message,
              required: true,
            })}
          </div>
          {/* Stop loss — editing this fires the "SL Updated" notification */}
          <div className={styles.formGroup}>
            {renderPriceFieldWithLtp({
              fieldName: "stopLoss",
              label: "Stop Loss",
              placeholder: "Enter stop loss",
              validation: { required: VALIDATION_MESSAGES.REQUIRED },
              error: errors.stopLoss?.message,
              required: true,
            })}
          </div>
          {/* Entry reasoning/notes; spans three columns */}
          <div
            className={`${styles.formGroup} ${styles.formGroupSpan3}`}
          >
            <Textarea
              label="Entry Note"
              placeholder="Enter entry notes"
              error={errors.entryNote?.message}
              {...register("entryNote", {
                required: VALIDATION_MESSAGES.REQUIRED,
                maxLength: { value: 1000 },
              })}
              required
              className={styles.textareaFull}
            />
          </div>
          {/* --- ROW 3 --- */}
          <div /> {/* blank */}
          <div /> {/* blank */}
          {/* Optional upper price of the entry range */}
          <div className={styles.formGroup}>
            <Input
              label="Reco Price High"
              type="number"
              step="0.01"
              placeholder="Enter high price"
              error={errors.recoPriceHigh?.message}
              {...register("recoPriceHigh", {
                min: { value: 0, message: "Price must be >= 0" },
              })}
            />
          </div>
          {/* Optional second profit target */}
          <div className={styles.formGroup}>
            <Input
              label="Target 2"
              type="number"
              step="0.01"
              placeholder="Enter target 2"
              error={errors.targetTwo?.message}
              {...register("targetTwo")}
            />
          </div>
          <div /> {/* STOP LOSS spacer */}
          {/* Entry Note spans here */}
          {/* --- ROW 4 --- */}
          <div /> {/* blank */}
          <div /> {/* blank */}
          <div /> {/* Reco high spacer */}
          {/* Notes written when the trade is exited/closed */}
          <div className={styles.formGroup}>
            <Textarea
              label="Exit Note"
              placeholder="Enter exit notes"
              error={errors.exitNote?.message}
              {...register("exitNote", {
                maxLength: { value: 2000 },
              })}


              className={styles.textareaExitNote}
            />
          </div>
          <div /> {/* blank */}
          {/* Entry Note spans here */}
          {/* --- ROW 5 (exit details: disabled while Live, required once closing) --- */}
          {/* How the trade was exited: Manual or Auto */}
          <div className={styles.formGroup}>
            <Select
              label="Exit Type"
              options={exitTypeIdOptions}
              placeholder="Select exit type"
              error={errors.exitTypeId?.message}
              {...register("exitTypeId", {
                required: !isLiveStatus && watchStatus ? VALIDATION_MESSAGES.REQUIRED : false,
              })}
              disabled={isLiveStatus}
            />
          </div>
          {/* Actual price the trade was exited at */}
          <div className={styles.formGroup}>
            <Input
              label="Exit Price Low"
              type="number"
              step="0.01"
              placeholder="Enter exit low price"
              error={errors.exitPriceLow?.message}
              {...register("exitPriceLow", {
                required: !isLiveStatus && watchStatus ? VALIDATION_MESSAGES.REQUIRED : false,
              })}
              disabled={isLiveStatus}
            />
          </div>
          {/* Actual exit date (pairs with entry date for duration) */}
          <div className={styles.formGroup}>
            <Input
              label="Actual Exit Date"
              type="date"
              placeholder="Select exit date"
              {...register("exitDate", {
                required: !isLiveStatus && watchStatus ? VALIDATION_MESSAGES.REQUIRED : false,
              })}
              disabled={isLiveStatus}
            />
          </div>
          {/* Actual exit time */}
          <div className={styles.formGroup}>
            <Input
              label="Actual Exit Time"
              type="time"
              placeholder="Select exit time"
              error={errors.exitTime?.message}
              {...register("exitTime", {
                required: !isLiveStatus && watchStatus ? VALIDATION_MESSAGES.REQUIRED : false,
              })}
              disabled={isLiveStatus}
            />
          </div>

          {/* Optional supporting document/chart image */}
          <div className={styles.formGroup}>
            <Input
              label="Attachment / File"
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              error={errors.attachment?.message}
              {...register("attachment")}
            />
          </div>
          {/* --- ROW 6 --- */}
          <div /> {/* blank */}
          {/* Optional upper exit price */}
          <div className={styles.formGroup}>
            <Input
              label="Exit Price High"
              type="number"
              step="0.01"
              placeholder="Enter exit high price"
              error={errors.exitPriceHigh?.message}
              {...register("exitPriceHigh")}
            />
          </div>
          <div /> {/* blank */}
          <div /> {/* blank */}
          {/* Exit Note spans here */}
          <div /> {/* blank */}
          {/* --- EXTRA FIELDS NOT IN IMAGE BUT REQUIRED --- */}
          {/* Per-target status dropdowns (used by edit-time change detection) */}
          <div
            className={styles.extraFieldsRow}
          >
            <div className={styles.flexOne}>
              <Select
                label="Target 1 Status"
                options={targetStatusOptions}
                placeholder="Select target 1 status"
                error={errors.targetOneStatus?.message}
                {...register("targetOneStatus")}
              />
            </div>
            <div className={styles.flexOne}>
              <Select
                label="Target 2 Status"
                options={targetStatusOptions}
                placeholder="Select target 2 status"
                error={errors.targetTwoStatus?.message}
                {...register("targetTwoStatus")}
              />
            </div>
            <div className={styles.flexOne}>
              <Select
                label="Target 3 Status"
                options={targetStatusOptions}
                placeholder="Select target 3 status"
                error={errors.targetThreeStatus?.message}
                {...register("targetThreeStatus")}
              />
            </div>
          </div>
        </div>
  );
};

export default TradeFormFields;
